import { callGetResumeById } from "api/MuyeonApi";
import { openNativeOrWeb, nativeHasCapability } from "common/nativeRoute";
import { canReceiveQuoteAs, chatFilterForActive } from "constants/permissions";
import { openMemberType } from "util/openMemberType";

/**
 * 링크 주소 → 앱 네이티브 화면 표 (2026-10-26).
 *  유니버셜 링크·앱 링크·알림톡으로 들어온 주소가 앱에 네이티브 화면이 있는 곳이면 웹 페이지 대신 그 화면을 연다.
 *  앱은 링크 경로를 window.__openLink(path) 로 넘기기만 한다(routes/AppRouter). 표는 여기 한 곳.
 *
 *  ⚠️ nativeRoute.js 의 WEB_FALLBACK(네이티브 화면 → 웹 주소)을 뒤집은 표다. 한쪽을 고치면 다른 쪽도 본다.
 *  ⚠️ 새 주소를 넣으면 앱이 그 링크를 받도록 public/.well-known/apple-app-site-association 과
 *     muyeon_aos AndroidManifest(앱 링크 인텐트 필터)에도 같은 경로를 넣는다.
 *
 *  넣지 않는 것: 만들기·고치기 화면(쓰던 글을 덮을 수 있다), 결제·가입·약관(웹에서 끝나야 하는 흐름),
 *   네이티브 화면이 없는 상세(공고·레슨 상세 — 웹 그대로).
 *
 *  행: path(웹 라우트 모양), when(쿼리 조건, 선택), ok(주소 조각 검사, 선택), capability(앱이 넣어 주는 기능 표시, 선택 — 없으면 웹),
 *   action(nativeBridge 이름), payload(params, query, ctx).
 *   열기 전에 서버 값이 필요한 화면은 action/payload 대신 run(navigate, ctx) — 웹 버튼이 쓰는 함수를 그대로 부른다.
 *  위에서부터 처음 맞는 행을 쓴다.
 */
const resumeMode = (q, ctx) => ({ mode: q.get("as") === "dancer" || (q.get("as") !== "teacher" && ctx.activeType === "DANCER") ? "dancer" : "teacher" });

const NATIVE_LINKS = [
  /* 프로필·후기 */
  { path: "/teachers/:id", when: (q) => q.get("review") === "1", action: "openReviewWrite", payload: (p) => ({ teacherId: Number(p.id) }) },
  { path: "/teachers/:id", action: "openPublicProfile", payload: (p, q) => ({ userId: Number(p.id), ...(q.get("src") ? { src: q.get("src") } : {}) }) },
  { path: "/dancers/:id", action: "openPublicProfile", payload: (p) => ({ userId: Number(p.id), mode: "dancer" }) },
  { path: "/academies/:id", action: "openAcademyProfile", payload: (p, q) => ({ academyId: Number(p.id), ...(q.get("src") ? { src: q.get("src") } : {}) }) },
  { path: "/jobs/:jobId/applicants/:applicationId", action: "openApplicantResume", payload: (p) => ({ jobId: Number(p.jobId), applicationId: Number(p.applicationId) }) },
  { path: "/subs/:subId/applicants/:applicationId", action: "openApplicantResume", payload: (p) => ({ subId: Number(p.subId), applicationId: Number(p.applicationId) }) },

  /* 이력서·내 공고 */
  { path: "/resume", action: "openResumeList", payload: (_p, q, ctx) => resumeMode(q, ctx) },
  { path: "/profileResume", action: "openResumeList", payload: (_p, q, ctx) => resumeMode(q, ctx) },
  { path: "/fieldVisibility", action: "openFieldVisibility", payload: (_p, q, ctx) => resumeMode(q, ctx) },
  {
    path: "/resumeEdit",
    when: (q) => !q.has("id") || /^[1-9]\d*$/.test(q.get("id")),
    run: async (navigate, ctx, q) => {
      const payload = { ...resumeMode(q, ctx), ...(q.get("seekProfile") === "1" ? { seekProfile: "1" } : {}) };
      if (q.has("id")) {
        // 기존 문서는 현재 활동유형·링크 추측보다 저장된 역할이 우선이다.
        if (!ctx.user) return navigate(`/login?redirect=${encodeURIComponent(`/resumeEdit?${q}`)}`);
        try {
          const r = await callGetResumeById(Number(q.get("id")));
          const doc = r?.data;
          if (!doc?.id) throw new Error("missing resume");
          payload.resumeId = doc.id;
          if (["TEACHER", "DANCER"].includes(doc.data?.roleIntent)) payload.mode = doc.data.roleIntent === "DANCER" ? "dancer" : "teacher";
        } catch (e) { return navigate(`/resumeEdit?${q}`); }
      }
      return openNativeOrWeb("openResumeEdit", payload, navigate);
    },
  },
  { path: "/myPostings", action: "openMyJobPostings", payload: () => ({}) },

  /* 견적 */
  {
    path: "/quoteRequest",
    action: "openQuoteWizard",
    payload: (_p, q) => ({
      ...(q.get("category") ? { category: q.get("category") } : {}),
      ...(q.get("teacherId") ? { targetTeacherId: Number(q.get("teacherId")) } : {}),
    }),
  },
  { path: "/availableQuotes", action: "openQuoteBrowse", payload: () => ({}) },
  { path: "/quoteSettings", action: "openAutoQuoteTemplates", payload: () => ({}) },
  { path: "/autoQuoteHistory", action: "openAutoQuoteHistory", payload: (_p, q) => (q.get("month") ? { month: q.get("month") } : {}) },

  /* 레슨 */
  { path: "/lessonCalendar", action: "openLessonCalendar", payload: (_p, q) => (q.get("lessonId") ? { lessonId: Number(q.get("lessonId")) } : {}) },
  { path: "/lessonHub", action: "openLessonQuoteHub", payload: (_p, _q, ctx) => ({ role: ctx.activeType }) },
  { path: "/reservations/:id", action: "openLessonReservationDetail", payload: (p) => ({ reservationId: Number(p.id) }) },
  { path: "/lessonSlots", action: "openLessonSlotManage", payload: (_p, q) => (q.get("lessonId") ? { lessonId: Number(q.get("lessonId")) } : {}) },

  /* 학원 소속·멤버십 */
  { path: "/my/academyTeachers", action: "openAcademyTeachers", payload: () => ({}) },
  { path: "/my/academyInvites", action: "openAcademyInvites", payload: () => ({}) },
  { path: "/membership/:featureType", action: "openMembership", payload: () => ({ featureType: "MEMBERSHIP" }) },
  { path: "/myMembership/performance", action: "openMembershipPerformance", payload: (_p, _q, ctx) => ({ memberType: ctx.activeType }) },
  // 회원유형 관리 — 보유 유형·배경 이미지를 서버에서 받아 연다(웹 버튼과 같은 util/openMemberType).
  { path: "/my/memberType", run: (navigate, ctx) => openMemberType(navigate, ctx.activeType || undefined) },

  /* 캐스팅·무대 (10-27) — 첫 화면은 nativeCastingStage 앱이면 열린다. 안쪽 화면은 payload 를 읽는 앱(castingStageLink)만 —
     그 전 앱은 payload 를 버리고 첫 화면만 열어 공고 상세를 잃으므로 웹 페이지로 둔다.
     등록(/casting/create)·지원자 이력서는 넣지 않는다. 숫자 아닌 /casting/:id 는 ok 로 거른다. */
  { path: "/casting", capability: "nativeCastingStage", action: "openCastingStage", payload: () => ({}) },
  { path: "/casting/all", capability: "castingStageLink", action: "openCastingStage", payload: () => ({ screen: "castingList" }) },
  { path: "/casting/stage", capability: "castingStageLink", action: "openCastingStage", payload: () => ({ screen: "issues" }) },
  {
    path: "/casting/stage/:issueNo",
    ok: (p) => /^\d+$/.test(p.issueNo),
    capability: "castingStageLink",
    action: "openCastingStage",
    payload: (p) => ({ issueNo: p.issueNo }),
  },
  {
    path: "/casting/stage/:issueNo/:category",
    ok: (p) => /^\d+$/.test(p.issueNo) && /^[A-Z_]+$/.test(p.category),
    capability: "castingStageLink",
    action: "openCastingStage",
    payload: (p) => ({ issueNo: p.issueNo, category: p.category }),
  },
  { path: "/casting/:id", ok: (p) => /^\d+$/.test(p.id), capability: "castingStageLink", action: "openCastingStage", payload: (p) => ({ castingId: p.id }) },

  /* 공간 대관·무용센터 양도양수 (11-03) — 새 앱(capability)만 네이티브, 옛 앱은 웹 화면. 등록·수정 주소는 넣지 않는다.
     then(로그인 후 이어서 할 일)·date 는 그대로 넘긴다. 숫자 아닌 /spaces/create 등은 ok 로 거른다. */
  { path: "/spaces", capability: "nativeSpaceBooking", action: "openSpaces", payload: () => ({}) },
  {
    path: "/spaces/:id",
    ok: (p) => /^\d+$/.test(p.id),
    capability: "nativeSpaceBooking",
    action: "openSpaceDetail",
    payload: (p, q) => ({ spaceId: Number(p.id), ...(q.get("date") ? { date: q.get("date") } : {}), ...(q.get("then") ? { then: q.get("then") } : {}) }),
  },
  { path: "/mySpaces", capability: "nativeSpaceBooking", action: "openMySpaces", payload: () => ({}) },
  { path: "/mySpaceReservations", capability: "nativeSpaceBooking", action: "openMySpaceReservations", payload: () => ({}) },
  { path: "/mySpaceBookings", capability: "nativeSpaceBooking", action: "openMySpaceBookings", payload: () => ({}) },
  { path: "/studio-transfers", capability: "nativeStudioTransfers", action: "openStudioTransfers", payload: () => ({}) },
  {
    path: "/studio-transfers/:id",
    ok: (p) => /^\d+$/.test(p.id),
    capability: "nativeStudioTransfers",
    action: "openStudioTransferDetail",
    payload: (p, q) => ({ id: Number(p.id), ...(q.get("then") ? { then: q.get("then") } : {}) }),
  },
  { path: "/myStudioTransfers", capability: "nativeStudioTransfers", action: "openMyStudioTransfers", payload: () => ({}) },

  /* 스튜디오 운영(11-04 웹 화면 신설) — 앱은 iOS·AOS 모두 네이티브가 있어 capability 없이 넘긴다. 회원 상세(/studio/members/:id)는 네이티브 진입점이 없어 웹 그대로. */
  { path: "/studio", action: "openStudioOps", payload: () => ({}) },
  { path: "/studio/members", action: "openStudioMembers", payload: () => ({}) },
  { path: "/studio/sales", action: "openStudioSales", payload: () => ({}) },
  { path: "/studio/schedule", action: "openStudioSchedule", payload: () => ({}) },

  /* 알림·채팅 목록 */
  { path: "/notifications", action: "openNotifications", payload: () => ({}) },
  { path: "/notificationSettings", action: "openNotificationSettings", payload: () => ({}) },
  { path: "/chat", action: "openChatList", payload: (_p, _q, ctx) => ({ filter: chatFilterForActive(ctx.activeType) }) },
];

/** '/teachers/:id' 같은 단순 모양만 맞춘다(별표·선택 조각 없음). 맞으면 params, 아니면 null. */
const matchPath = (pattern, pathname) => {
  const a = String(pattern).replace(/^\/+|\/+$/g, "").split("/");
  const b = String(pathname).replace(/^\/+|\/+$/g, "").split("/");
  if (a.length !== b.length) return null;
  const params = {};
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].startsWith(":")) {
      if (!b[i]) return null;
      params[a[i].slice(1)] = decodeURIComponent(b[i]);
    } else if (a[i] !== b[i]) return null;
  }
  return { params };
};

/** 웹 라우트 정의(routes/index privateRouteDefs)에서 이 경로의 meta 를 찾는다. */
const routeMetaOf = (pathname, routeDefs) => {
  const def = (routeDefs || []).find((d) => d?.path && matchPath(d.path, pathname));
  return def?.meta || {};
};

/**
 * 링크 경로에 맞는 네이티브 화면 여는 함수를 돌려준다. 없거나 보호 장치를 통과하지 못하면 null — 호출부가 웹 페이지로 연다.
 *  보호 장치는 웹 라우트의 meta 를 그대로 쓴다: auth(로그인) · lessonProvider(강사·학원 원장 유형).
 *  통과 못 하면 웹 페이지로 열어야 웹이 원래 띄우던 안내(로그인 화면·'레슨은 강사·학원 원장 유형에서…')가 그대로 뜬다.
 */
export function matchNativeLink(path, { navigate, user, activeType, routeDefs }) {
  let url;
  try {
    url = new URL(path, window.location.origin);
  } catch (e) {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  const q = url.searchParams;
  let row = null;
  let params = null;
  for (const r of NATIVE_LINKS) {
    const m = matchPath(r.path, url.pathname);
    if (m && (!r.when || r.when(q)) && (!r.ok || r.ok(m.params))) { row = r; params = m.params; break; }
  }
  if (!row) return null;
  if (row.capability && !nativeHasCapability(row.capability)) return null;

  const meta = routeMetaOf(url.pathname, routeDefs);
  if (meta.auth && !meta.guest && !user) return null;
  if (meta.lessonProvider && !canReceiveQuoteAs(activeType)) return null;

  if (row.run) return () => row.run(navigate, { activeType, user }, q);
  const payload = row.payload(params, q, { activeType });
  return () => openNativeOrWeb(row.action, payload, navigate);
}

export const __test = { NATIVE_LINKS };
